import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  Dispatch,
  SetStateAction,
  use,
} from "react";
import {
  fetchOrderDetailsByIdApi,
  fetchProductByIdApi,
  fetchProductsByBrandIdApi,
  getBrandOrders,
  getUserAssociatedBrand,
  getUserDetails,
  updateShipmentsApi,
  updateUserDetails,
} from "../api/userApi"; // Make sure to import your update function
import { changePassword, notify } from "../api/authApi";

export const UserContext = createContext<UserContextProps | undefined>(
  undefined
);

export const UserContextProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [userData, setUserData] = useState<UserData>({
    id: "",
    name: "",
    email: "",
    passwordHash: "",
    phone: "",
    gender: "",
    dob: "",
  });
  const [shipmentData, setShipmentData] = useState<ShipmentData | {}>({});
  const [loading, setLoading] = useState(false);
  const [brandData, setBrandData] = useState<BrandData>([]);
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  const [orderData, setOrderData] = useState<Order[]>([]);
  const [productData, setProductData] = useState<BrandProduct[]>([]);
  const [isDetailsPageLoading, setIsDetailsPageLoading] =
    useState<boolean>(false);
  const [productDetails, setProductDetails] = useState<Product[]>([]);
  const [orderDetails, setOrderDetails] = useState<OrderDetails | {}>({});

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const email = localStorage.getItem("email");
      if (email) {
        const response = await getUserDetails(email);
        if (response.id) {
          setUserData(response);
        } else {
          notify("Token is expired");
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserAssociatedBrand = async (userId: string) => {
    setLoading(true);
    try {
      const response = await getUserAssociatedBrand(userId);
      const data = response?.data?.[0]?.brands;
      setBrandData(data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBrandOrders = async (brandId: string) => {
    setLoading(true);
    try {
      const response = await getBrandOrders(brandId);
      setOrderData(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUserDetails = async (
    id: string,
    data: UserData,
    setOpenProfileDrawer?: Dispatch<SetStateAction<boolean>>
  ) => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      if (token) {
        const response = await updateUserDetails(id, data);
        setTimeout(() => {
          setUserData(response);
          setLoading(false);
          setOpenProfileDrawer && setOpenProfileDrawer(false);
          notify("Userdata Updated");
        }, 1500);
      } else {
        throw Error("Token is expired");
      }
    } catch (error) {
      console.error("Failed to update user details", error);
      notify("Failed to update user details");
      setLoading(false);
    }
  };

  const handleUpdateUserPassword = async (
    email: string,
    password: string,
    newpassword: string,
    setOpenProfileDrawer?: Dispatch<SetStateAction<boolean>>
  ) => {
    try {
      setLoading(true);
      const response = await changePassword(email, password, newpassword);
      setTimeout(() => {
        setLoading(false);
        setOpenProfileDrawer && setOpenProfileDrawer(false);
        notify("Userdata Updated");
      }, 1500);
    } catch (error) {
      console.error("Failed to update user details", error);
      notify("Failed to update user details");
      setLoading(false);
    }
  };

  const fetchOrderDetailsById = async (orderId: string) => {
    setProductDetails([]);
    setIsDetailsPageLoading(true);
    try {
      const data = await fetchOrderDetailsByIdApi(orderId);
      setOrderDetails(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchProductDetailsById = async (productId: string) => {
    setIsDetailsPageLoading(true);
    try {
      const data = await fetchProductByIdApi(productId);
      setProductDetails((prev) => {
        const isProductExists = prev.some(
          (product) => product._id === data._id
        );

        return isProductExists ? prev : [...prev, data];
      });
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setIsDetailsPageLoading(false);
    }
  };

  const handleBrandSelection = (brand: Brand) => {
    console.log("===CALLLING",brand)
    setSelectedBrand(brand);
  };

  useEffect(() => {
    fetchUserDetails();
  }, []);

  const updateShipmentsStatus = async (
    orderId: string,
    payload: any
  ) => {
    try {
      const data = await updateShipmentsApi(orderId, payload);
      setShipmentData(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const fetchBrandProducts = async (brandId: string) => {
    setLoading(true);
    try {
      const response = await fetchProductsByBrandIdApi(brandId);
      setProductData(response.product);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <UserContext.Provider
      value={{
        userData,
        loading,
        fetchUserDetails,
        updateUserDetails: handleUpdateUserDetails,
        handleUpdateUserPassword,
        fetchUserAssociatedBrand,
        brandData,
        selectedBrand,
        handleBrandSelection,
        fetchBrandOrders,
        orderData,
        fetchOrderDetailsById,
        fetchProductDetailsById,
        orderDetails,
        setOrderDetails,
        productDetails,
        setProductDetails,
        isDetailsPageLoading,
        updateShipmentsStatus,
        shipmentData,
        fetchBrandProducts,
        productData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserContextProvider");
  }
  return context;
};
